import { GoogleGenerativeAI } from "@google/generative-ai";
import { hasCJK, looksMostlyEnglish, stripParentheticalTransliteration } from './languageHeuristics';

const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const DEFAULT_MODEL_ID = "gemini-3.1-flash-lite-preview";

const getGenAI = () => {
    if (!API_KEY) {
        throw new Error("REACT_APP_GEMINI_API_KEY is not set. Please create a .env file in the project root with the API key.");
    }
    return new GoogleGenerativeAI(API_KEY);
}

const getModel = () => {
    const genAI = getGenAI();
    return genAI.getGenerativeModel({
        model: DEFAULT_MODEL_ID,
    });
}

const getAudioModel = () => getModel();

const getImageModel = () => getModel();

const HTML_TAG_PATTERN = /<\/?[a-z][^>]*>/i;
const OUTPUT_LABEL_LINE_PATTERN = /^\s*[*_`#>\- ]*(?:refined|expanded|corrected|final)\s+(?:html(?:\s*(?:structure|construction|output))?|text|content|output|polish(?:\s+of\s+html\s+structure)?)\s*[*_`.:\- ]*$/i;
const OUTPUT_LABEL_INLINE_PATTERN = /(?:^|\n)\s*[*_`#>\- ]*(?:refined|expanded|corrected|final)\s+(?:html(?:\s*(?:structure|construction|output))?|text|content|output|polish(?:\s+of\s+html\s+structure)?)\s*[*_` ]*:\s*[*_` ]*([\s\S]+)$/i;
const ANALYSIS_PREAMBLE_PATTERN = /^\s*(?:[-*•]\s*)?[*_` ]*(?:input|task|constraint(?:\s+\d+)?|tone shift|structure|methodology|drafting(?:\s+para\s+\d+)?|para\s+\d+(?:\/\d+)?|check|self-correction(?:\s+during\s+drafting)?|handling the image placeholder|wait)\b/i;

const stripCodeFence = (responseText = '') => {
    const trimmed = responseText.trim();

    if (!trimmed) {
        return '';
    }

    const fullFenceMatch = trimmed.match(/^```(?:[a-z0-9_-]+)?\s*([\s\S]*?)\s*```$/i);
    if (fullFenceMatch) {
        return fullFenceMatch[1].trim();
    }

    const fencedBlockMatch = trimmed.match(/```(?:[a-z0-9_-]+)?\s*([\s\S]*?)\s*```/i);
    if (!fencedBlockMatch) {
        return trimmed;
    }

    const outerText = trimmed.replace(fencedBlockMatch[0], '').trim();
    if (!outerText || /^(?:here(?:'s| is)?|below|result|output|refined|expanded|formatted|html|text|content|draft|sure|certainly|requested|response|the|following|is|your|:|-|\s)+$/i.test(outerText)) {
        return fencedBlockMatch[1].trim();
    }

    return trimmed;
};

const stripHtmlDocumentWrappers = (responseText = '') => responseText
    .replace(/<!DOCTYPE[^>]*>/gi, '')
    .replace(/<head[\s\S]*?<\/head>/gi, '')
    .replace(/<\/?(?:html|body)>/gi, '')
    .trim();

// Removes any line that is a model chain-of-thought marker (* *Label:* style)
// but keeps lines that contain actual HTML tags alongside the marker.
const stripModelAnalysisLines = (text = '') => {
    if (!text) return '';
    return text
        .replace(/\r\n/g, '\n')
        .split('\n')
        .filter((line) => !/\*\s+\*/.test(line) || HTML_TAG_PATTERN.test(line))
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

const extractLabeledOutput = (responseText = '') => {
    const normalizedText = responseText.replace(/\r\n/g, '\n').trim();
    if (!normalizedText) {
        return '';
    }

    const lines = normalizedText.split('\n');
    let labeledStartIndex = -1;

    lines.forEach((line, index) => {
        if (OUTPUT_LABEL_LINE_PATTERN.test(line.trim())) {
            labeledStartIndex = index + 1;
        }
    });

    if (labeledStartIndex !== -1) {
        return lines.slice(labeledStartIndex).join('\n').trim();
    }

    const inlineLabelMatch = normalizedText.match(OUTPUT_LABEL_INLINE_PATTERN);
    if (inlineLabelMatch) {
        return inlineLabelMatch[1].trim();
    }

    return normalizedText;
};

const stripAnalysisPreamble = (responseText = '') => {
    const normalizedText = responseText.replace(/\r\n/g, '\n').trim();
    if (!normalizedText) {
        return '';
    }

    const lines = normalizedText.split('\n');
    let currentIndex = 0;
    let matchedLines = 0;

    while (currentIndex < lines.length && !lines[currentIndex].trim()) {
        currentIndex += 1;
    }

    while (currentIndex < lines.length && ANALYSIS_PREAMBLE_PATTERN.test(lines[currentIndex].trim())) {
        matchedLines += 1;
        currentIndex += 1;
    }

    if (matchedLines < 2) {
        return normalizedText;
    }

    while (currentIndex < lines.length && !lines[currentIndex].trim()) {
        currentIndex += 1;
    }

    const candidate = lines.slice(currentIndex).join('\n').trim();
    return candidate || normalizedText;
};

const escapeHtml = (text = '') => text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatInlineText = (text = '') => {
    let formatted = escapeHtml(text.trim());
    formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/__(.+?)__/g, '<strong>$1</strong>');
    formatted = formatted.replace(/`([^`]+)`/g, '<code>$1</code>');
    return formatted;
};

const convertPlainTextToHtml = (responseText = '') => {
    const lines = responseText.replace(/\r\n/g, '\n').split('\n');
    const htmlBlocks = [];
    let paragraphLines = [];
    let listItems = [];
    let listTag = null;

    const flushParagraph = () => {
        if (!paragraphLines.length) {
            return;
        }

        htmlBlocks.push(`<p>${paragraphLines.map(formatInlineText).join('<br />')}</p>`);
        paragraphLines = [];
    };

    const flushList = () => {
        if (!listItems.length || !listTag) {
            return;
        }

        htmlBlocks.push(`<${listTag}>${listItems.map((item) => `<li>${formatInlineText(item)}</li>`).join('')}</${listTag}>`);
        listItems = [];
        listTag = null;
    };

    lines.forEach((rawLine) => {
        const line = rawLine.trim();

        if (!line) {
            flushParagraph();
            flushList();
            return;
        }

        const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
        if (headingMatch) {
            flushParagraph();
            flushList();
            const level = Math.min(3, headingMatch[1].length);
            htmlBlocks.push(`<h${level}>${formatInlineText(headingMatch[2])}</h${level}>`);
            return;
        }

        const orderedListMatch = line.match(/^\d+[.)]\s+(.*)$/);
        if (orderedListMatch) {
            flushParagraph();
            if (listTag && listTag !== 'ol') {
                flushList();
            }
            listTag = 'ol';
            listItems.push(orderedListMatch[1]);
            return;
        }

        const unorderedListMatch = line.match(/^[-*•]\s+(.*)$/);
        if (unorderedListMatch) {
            flushParagraph();
            if (listTag && listTag !== 'ul') {
                flushList();
            }
            listTag = 'ul';
            listItems.push(unorderedListMatch[1]);
            return;
        }

        if (listItems.length) {
            flushList();
        }

        paragraphLines.push(line);
    });

    flushParagraph();
    flushList();

    return htmlBlocks.join('');
};

const cleanHtmlResponse = (responseText = '') => {
    const afterCodeFence = stripHtmlDocumentWrappers(stripCodeFence(responseText));
    if (!afterCodeFence) return '';

    const afterLabelExtract = extractLabeledOutput(afterCodeFence);
    const afterAnalysisStrip = stripModelAnalysisLines(afterLabelExtract);

    if (HTML_TAG_PATTERN.test(afterAnalysisStrip)) {
        return afterAnalysisStrip;
    }

    return convertPlainTextToHtml(stripAnalysisPreamble(afterAnalysisStrip));
};



export async function translateText(text, targetLanguageName, targetLanguageCode) {
    const model = getModel();

    // Build an enhanced, explicit prompt
    const basePrompt = `You are a professional human-quality translator.
TASK: Translate the SOURCE text into "${targetLanguageName}" (language code: ${targetLanguageCode}).

QUALITY & STYLE REQUIREMENTS:
1. Output MUST be 100% in ${targetLanguageName} only. Do not include any other language.
2. Produce fluent, natural, idiomatic ${targetLanguageName} that reads as if originally written in ${targetLanguageName}, not a word-for-word literal translation.
3. Preserve meaning, tone, nuance, factual accuracy, numbers, formatting cues, punctuation, line breaks and paragraph structure.
4. Improve clarity and flow subtly where needed WITHOUT adding new facts or removing important detail.
5. Retain proper nouns, trademarks, technical terms, code snippets, URLs, statutes and case names in their original form if no standard localized equivalent exists.
6. DO NOT add explanations, notes, transliteration, pronunciation guides, brackets, quotes around the whole output, or any meta commentary.
7. If the source text is already fully in ${targetLanguageName}, return it unchanged except for minor punctuation/spacing normalization.
8. Never output markdown fences, JSON, or labels; ONLY the clean translated text.

SOURCE TEXT:
"""
${text}
"""

Return ONLY the translated text.`;

    async function attempt(prompt) {
        const result = await model.generateContent({
            contents: [{ role: "user", parts: [{ text: prompt }] }],
            generationConfig: { maxOutputTokens: 2048, temperature: 0.2 },
        });
        let out = result.response.candidates[0]?.content?.parts?.[0]?.text || '';
        out = stripParentheticalTransliteration(out);
        return out;
    }

    try {
        let translated = await attempt(basePrompt);

        // ENGLISH SPECIFIC ENFORCEMENT (reported bug: Chinese instead of English)
        if (targetLanguageCode === 'en') {
            if (hasCJK(translated) || !looksMostlyEnglish(translated)) {
                const enforcePrompt = `${basePrompt}

IMPORTANT: Your previous attempt incorrectly included non-English (e.g., Chinese) characters. Retry and ensure EVERY character (aside from proper nouns or numbers) is standard English alphabetic text. Output only the corrected English translation.`;
                const second = await attempt(enforcePrompt, 'retry');
                if (!hasCJK(second) && looksMostlyEnglish(second)) {
                    translated = second.trim();
                }
            }
        }

        return translated;
    } catch (error) {
        console.error("Error translating text with Gemini:", error);
        throw new Error("Translation failed. Please try again.");
    }
}
export async function fixSpellingAndGrammar(text) {
    const model = getModel(); // Use the model from getModel() which is now gemini-2.0-flash-lite

    try {
        const result = await model.generateContent({
            contents: [{
                role: "user", parts: [{
                    text: `Please correct the spelling and grammar of the following HTML content. Keep the original meaning, tone, and all HTML tags (like <p>, <b>, <h1>, etc.) and their structure intact. Only return the corrected HTML content, without any additional comments or explanations.

Original HTML:
${text}

Corrected HTML:` }]
            }],
            generationConfig: {
                maxOutputTokens: 8192,
                temperature: 0.2,
            },
        });
        const response = result.response;
        return cleanHtmlResponse(response.candidates[0].content.parts[0].text);
    } catch (error) {
        console.error("Error fixing spelling and grammar:", error);
        throw new Error("Failed to fix spelling and grammar. Please try again.");
    }
}

export async function refineTone(text) {
    const model = getModel();

    try {
        const result = await model.generateContent({
            contents: [{
                role: "user", parts: [{
                    text: `Refine the following content for clarity, conciseness, logical flow, and professionalism while preserving all facts, names, dates, figures, quotations, and domain accuracy. Do not add new facts.

STYLE RULES:
1. If the source is legal, preserve legal terminology and a formal drafting tone.
2. If the source is not legal, use a polished professional tone appropriate to the original subject and audience.

CRITICAL OUTPUT RULES — violations will break the editor:
1. Your ENTIRE response must be the refined HTML document. No preamble, no analysis, no notes, no chain-of-thought.
2. Do NOT produce any of the following: task descriptions, reasoning steps, constraints lists, checks, drafting notes, bullet points (* *...*), intermediate drafts, or any text outside valid HTML tags.
3. Return ONLY Quill-compatible semantic HTML using tags such as <p>, <h2>, <h3>, <ol>, <ul>, <li>, <strong>, <em>, and <blockquote> where appropriate.
4. No markdown fences, inline CSS, <html>, <body>, or document declarations.

Original HTML:
${text}

Refined HTML:` }]
            }],
            generationConfig: {
                maxOutputTokens: 8192,
                temperature: 0.2,
            },
        });
        const response = result.response;
        return cleanHtmlResponse(response.candidates[0].content.parts[0].text);
    } catch (error) {
        console.error("Error refining tone:", error);
        throw new Error("Failed to refine tone. Please try again.");
    }
}

export async function expandContent(text) {
    const model = getModel(); // Use gemini-1.5-flash model

    try {
        const result = await model.generateContent({
            contents: [{
                role: "user", parts: [{
                    text: `Expand the following editor content with more detail and explanation while preserving its meaning and document structure.

OUTPUT REQUIREMENTS:
1. Return ONLY Quill-compatible semantic HTML.
2. Use tags such as <p>, <h2>, <h3>, <ol>, <ul>, <li>, <strong>, <em>, and <blockquote> only when useful.
3. Do not return markdown, code fences, inline CSS, <html>, <body>, or any conversational filler.
4. Keep the result readable inside a rich-text editor with clear paragraphs and lists.

Original Content:
${text}

Expanded HTML:` }]
            }],
            generationConfig: {
                maxOutputTokens: 8192,
                temperature: 0.2,
            },
        });
        const response = result.response;
        return cleanHtmlResponse(response.candidates[0].content.parts[0].text);
    } catch (error) {
        console.error("Error expanding content:", error);
        throw new Error("Failed to expand content. Please try again.");
    }
}

export async function generateDraft(userPrompt) {
    const model = getModel(); // Use gemini-1.5-flash model

    try {
        const result = await model.generateContent(`Generate a detailed and comprehensive legal document based on the following requirements. Create a professionally formatted HTML document with proper legal styling and structure.

FORMATTING REQUIREMENTS:
1. Use a professional legal document layout with proper spacing and typography
2. Apply the following CSS styling inline for a polished appearance:
   - Document container: max-width: 800px, margin: 0 auto, padding: 40px, font-family: 'Times New Roman', serif, line-height: 1.6, color: #333
   - Main headings (h1): font-size: 18px, font-weight: bold, text-align: center, margin: 20px 0, text-transform: uppercase, letter-spacing: 1px
   - Section headings (h2): font-size: 16px, font-weight: bold, margin: 18px 0 12px 0, text-decoration: underline
   - Sub-headings (h3): font-size: 14px, font-weight: bold, margin: 15px 0 8px 0
   - Paragraphs: margin-bottom: 15px, text-align: justify, text-indent: 20px for body paragraphs
   - Legal clauses: margin: 10px 0, padding-left: 20px
   - Numbered lists: margin: 10px 0, padding-left: 30px
   - Signature blocks: margin-top: 40px, display: flex, justify-content: space-between
   - Court details: text-align: center, font-weight: bold, margin-bottom: 20px
   - Case numbers: text-align: center, margin: 10px 0
   - Party names: margin: 15px 0, font-weight: bold
   - Prayer section: margin-top: 25px
   - Verification: margin-top: 30px, font-style: italic

3. Structure the document with proper legal formatting:
   - Court name and jurisdiction at the top
   - Case number and year
   - Party details with proper formatting
   - Document title in caps and centered
   - Numbered paragraphs for facts and legal points
   - Proper prayer section
   - Signature blocks with adequate spacing
   - Verification clause at the end

4. Use appropriate legal typography:
   - Bold for important terms, headings, and party names
   - Italics for case citations and legal terms
   - Proper spacing between sections
   - Professional indentation for sub-clauses
   - Clear separation between different document sections

5. Ensure the document looks court-ready and professional with:
   - Consistent formatting throughout
   - Proper alignment and spacing
   - Professional color scheme (black text on white background)
   - Adequate margins and padding
   - Clean, readable typography

CONTENT REQUIREMENTS:
${userPrompt}

Generate ONLY the complete HTML document with inline CSS styling. Do not include any explanations, comments, or additional text outside the HTML.`);
        const response = result.response;
        return cleanHtmlResponse(response.candidates[0].content.parts[0].text);
    } catch (error) {
        console.error("Error generating draft:", error);
        throw new Error("Failed to generate draft. Please try again.");
    }
}

export async function askGemini(conversationHistory) {
    const model = getModel();

    // Define the persistent AI persona and directives
    const AI_PROMPT_PREFIX = `
# PERSONA
You are 'NyayaAI', an AI legal analyst specializing in the laws of India. Your expertise covers statutory law, case law from the Supreme Court and High Courts, and general legal principles.

# DIRECTIVES
1.  **Language Adaptation:** ALWAYS detect the language of the user's query and respond ONLY in that same language. For example, if the user asks in Marathi, respond in Marathi; if in Hindi, respond in Hindi; if in English, respond in English.
2.  **Analyze Thoroughly:** Provide a detailed and well-structured analysis of the user's query. Prioritize accuracy and comprehensive reasoning over simple conciseness.
2.  **Cite Sources:** Where possible, cite the relevant section of a statute (e.g., Section 420 of the Indian Penal Code, 1860) or a key Supreme Court precedent that establishes the legal principle.
3.  **Clarify Ambiguity:** If the user's query is ambiguous, incomplete, or lacks critical context, your **FIRST and ONLY** step is to ask specific, targeted clarifying questions to get the necessary information. **Do NOT provide any analysis until you have sufficient information.** For example, if asked about a real estate dispute, you might ask:
    *   Is the project registered under the Real Estate (Regulation and Development) Act, 2016 (RERA)?
    *   Does your 'Agreement for Sale' explicitly mention a penalty or interest for delayed possession?
    *   Have you sent any formal communication (like a letter or email) to the builder about the delay?
4.  **Structure Your Response (Only after clarification, if needed):** Once you have gathered sufficient information, organize your answer into the following sections:
    *   **A. Core Legal Principles:** Briefly explain the fundamental laws and legal principles that govern the query.
    *   **B. Application to the Query:** Apply these principles to the user's specific situation.
    *   **C. Nuances & Considerations:** Discuss any important exceptions, "it depends on" factors, or practical considerations.

# BOUNDARIES
Always conclude your response with the following mandatory disclaimer: "This analysis is for informational and educational purposes only, based on the information provided. It does not constitute legal advice. You should consult with a qualified lawyer licensed in India for advice on your specific situation."

# CONVERSATION START
`;

    // Map the conversation history to the Gemini API's 'contents' format
    const contents = conversationHistory.map((msg, index) => {
        let textContent = msg.text;
        // Prepend the AI_PROMPT_PREFIX to the very first user message only
        if (index === 0 && msg.sender === 'user') {
            textContent = AI_PROMPT_PREFIX + "Analyze the following query: " + textContent + "\n\nProvide the response as a single block of professionally formatted HTML, styled with inline CSS for a beautiful UI. Do not include any external CSS or JavaScript. Ensure paragraphs (<p>) have a bottom margin (e.g., 'margin-bottom: 12px;'). Use other tags like <b>, <i>, <ul>, and <li> as appropriate for clear, readable formatting. Only return the HTML content, without any additional comments or conversational filler.";
        }
        return {
            role: msg.sender === 'user' ? 'user' : 'model', // 'ai' sender becomes 'model' role
            parts: [{ text: textContent }],
        };
    });

    try {
        const result = await model.generateContent({
            contents: contents, // Pass the full conversation history
            generationConfig: {
                maxOutputTokens: 2048, // Adjust as needed
                temperature: 0.1, // Lower temperature for more factual responses
            },
        });
        const response = result.response;
        return cleanHtmlResponse(response.candidates[0].content.parts[0].text); // Clean the HTML response
    } catch (error) {
        console.error("Error asking Gemini:", error);
        throw new Error("Failed to get a response from the AI. Please try again.");
    }
}

export async function convertAudioToText(audioFile) {
    const model = getAudioModel();

    try {
        // Convert audio file to base64
        const audioData = await fileToGenerativePart(audioFile);

        const result = await model.generateContent({
            contents: [{
                role: "user",
                parts: [
                    { text: "Please transcribe this audio file to text. Provide only the transcribed text without any additional comments or explanations." },
                    audioData
                ]
            }],
            generationConfig: {
                maxOutputTokens: 8192,
                temperature: 0.1,
            },
        });

        const response = result.response;
        return response.candidates[0].content.parts[0].text;
    } catch (error) {
        console.error("Error converting audio to text:", error);
        throw new Error("Failed to convert audio to text. Please try again.");
    }
}

export async function convertImageToText(imageFile) {
    const model = getImageModel();

    try {
        // Convert image file to base64
        const imageData = await fileToGenerativePart(imageFile);

        const result = await model.generateContent({
            contents: [{
                role: "user",
                parts: [
                    { text: "Please extract and transcribe all text content from this image. Provide only the extracted text without any additional comments or explanations." },
                    imageData
                ]
            }],
            generationConfig: {
                maxOutputTokens: 8192,
                temperature: 0.1,
            },
        });

        const response = result.response;
        return response.candidates[0].content.parts[0].text;
    } catch (error) {
        console.error("Error converting image to text:", error);
        throw new Error("Failed to convert image to text. Please try again.");
    }
}

// Helper function to convert file to generative part
async function fileToGenerativePart(file) {
    const base64EncodedDataPromise = new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(file);
    });

    return {
        inlineData: {
            data: await base64EncodedDataPromise,
            mimeType: file.type
        }
    };
}